/**
 * <pre-order-countdown> — renders a compact, in-line live countdown to a
 * pre-order deadline.
 *
 * The element is seeded with a server-rendered fallback (so it is correct
 * without JS and causes no layout shift). When JS runs, all mounted
 * instances are updated by a single shared 1s ticker for efficiency on
 * collection pages that render many product cards.
 *
 * Attributes:
 *   data-deadline  - deadline as a UNIX timestamp in seconds (UTC). Required.
 *   data-unit-days / data-unit-hours / data-unit-minutes / data-unit-seconds
 *                  - localized short unit labels (e.g. "d"/"h"/"m"/"s").
 *   data-expired   - localized label shown once the deadline has passed.
 *
 * On expiry the element gets `data-state="expired"` and dispatches a
 * bubbling `preorder:expired` event so containers can react if desired.
 */
(function () {
  const TAG = 'pre-order-countdown';
  if (customElements.get(TAG)) return;

  /** @type {Set<PreOrderCountdown>} */
  const instances = new Set();
  let timerId = null;

  function tick() {
    const now = Date.now();
    for (const el of instances) el.update(now);
  }

  function startTicker() {
    if (timerId !== null) return;
    tick();
    timerId = setInterval(tick, 1000);
  }

  function stopTicker() {
    if (timerId === null || instances.size > 0) return;
    clearInterval(timerId);
    timerId = null;
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  class PreOrderCountdown extends HTMLElement {
    connectedCallback() {
      const deadline = parseInt(this.getAttribute('data-deadline'), 10);
      // Without a valid deadline we keep the server-rendered fallback as-is.
      if (!Number.isFinite(deadline)) return;

      this.deadlineMs = deadline * 1000;
      this.units = {
        days: this.getAttribute('data-unit-days') || 'd',
        hours: this.getAttribute('data-unit-hours') || 'h',
        minutes: this.getAttribute('data-unit-minutes') || 'm',
        seconds: this.getAttribute('data-unit-seconds') || 's',
      };
      this.expiredLabel = this.getAttribute('data-expired') || '';
      this.expired = false;

      instances.add(this);
      startTicker();
    }

    disconnectedCallback() {
      instances.delete(this);
      stopTicker();
    }

    update(now) {
      const remaining = this.deadlineMs - now;

      if (remaining <= 0) {
        if (this.expired) return;
        this.expired = true;
        this.setAttribute('data-state', 'expired');
        // Prefer hiding the whole pre-order group (badge / countdown line) so
        // we never render a contradictory "until … ended" string. Fall back to
        // an inline expired label for any standalone usage.
        const group = this.closest('[data-preorder-expire-hide]');
        if (group) {
          group.style.display = 'none';
        } else if (this.expiredLabel) {
          this.textContent = this.expiredLabel;
        }
        this.dispatchEvent(
          new CustomEvent('preorder:expired', { bubbles: true })
        );
        instances.delete(this);
        stopTicker();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // Always show down to seconds; drop only the leading empty units. The
      // leading unit is unpadded, the rest are zero-padded for a stable width.
      const u = this.units;
      let text;
      if (days > 0) {
        text =
          days + u.days + ' ' + pad(hours) + u.hours + ' ' +
          pad(minutes) + u.minutes + ' ' + pad(seconds) + u.seconds;
      } else if (hours > 0) {
        text =
          hours + u.hours + ' ' + pad(minutes) + u.minutes + ' ' +
          pad(seconds) + u.seconds;
      } else if (minutes > 0) {
        text = minutes + u.minutes + ' ' + pad(seconds) + u.seconds;
      } else {
        text = seconds + u.seconds;
      }

      // Avoid redundant DOM writes (the ticker fires every second).
      if (this.textContent !== text) this.textContent = text;
    }
  }

  customElements.define(TAG, PreOrderCountdown);
})();
